import { getCurrentInstance } from 'vue';
import { pushDataLayerEvent } from '@/utils/dataLayer';

/**
 * Composable for analytics tracking via GTM dataLayer
 * @returns {Object} Analytics tracking methods
 */
export function useAnalytics() {
  const instance = getCurrentInstance();
  const gtag = instance?.appContext.config.globalProperties.$gtag;

  if (!gtag && process.env.NODE_ENV === 'production') {
    console.warn('Analytics ($gtag) is not available');
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {Object} params - Event parameters
   */
  const trackEvent = (eventName, params = {}) => {
    pushDataLayerEvent(eventName, params);
  };

  /**
   * Track a page view (SPA route change)
   * @param {string} screenName - Name of the screen/page
   * @param {string} path - Current path
   */
  const trackScreenView = (screenName, path) => {
    pushDataLayerEvent('page_view', {
      page_title: screenName || document.title,
      page_path: path,
      page_location: `${window.location.origin}${path}`,
    });
  };

  /**
   * Track an error
   * @param {Error} error - Error object
   * @param {boolean} fatal - Whether the error is fatal
   */
  const trackError = (error, fatal = false) => {
    pushDataLayerEvent('exception', {
      description: error?.message,
      fatal,
    });
  };

  /**
   * Set custom dimensions (handled in GTM; kept for API compatibility)
   * @param {Object} dimensions - Custom dimensions to set
   */
  const setCustomDimensions = (dimensions = {}) => {
    pushDataLayerEvent('set_custom_dimensions', dimensions);
  };

  /**
   * Track e-commerce event
   * @param {string} eventName - E-commerce event name
   * @param {Object} params - Event parameters
   */
  const trackEcommerce = (eventName, params = {}) => {
    pushDataLayerEvent(eventName, params);
  };

  return {
    trackEvent,
    trackScreenView,
    trackError,
    setCustomDimensions,
    trackEcommerce,
  };
}
